import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import Select from "react-select";

const EditStudentModal = ({ isOpen, onClose, student, onSubmit }) => {
  // State variables for form fields
  const [studentId, setStudentId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [cohort, setCohort] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [parentName, setParentName] = useState("");
  const [parentPhone, setParentPhone] = useState("");
  const [program, setProgram] = useState("");

  // Tab state
  const [activeTab, setActiveTab] = useState("student");

  useEffect(() => {
    if (isOpen && student) {
      setStudentId(student.studentId || "");
      setFirstName(student.firstName || "");
      setLastName(student.lastName || "");
      setCohort(student.cohort ? student.cohort.toString() : "");
      setEmail(student.email || "");
      setPhone(student.phone || "");
      setStreet(student.address?.street || "");
      setCity(student.address?.city || "");
      setState(student.address?.state || "");
      setZip(student.address?.zip || "");
      setParentName(student.parentName || "");
      setParentPhone(student.parentPhone || "");
      setProgram(student.program || "");
    }
  }, [isOpen, student]);

  const renderStepContent = () => {
    switch (activeTab) {
      case "student":
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Student Information</h3>
            <div className="grid grid-cols-1 gap-4">
              {/* First Name */}
              <div>
                <label className="block text-gray-700">First Name</label>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              {/* Last Name */}
              <div>
                <label className="block text-gray-700">Last Name</label>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
              {/* Student ID */}
              <div>
                <label className="block text-gray-700">Student ID</label>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                  required
                />
              </div>
              {/* Cohort */}
              <div>
                <label className="block text-gray-700">Cohort</label>
                <input
                  type="number"
                  className="w-full border rounded px-3 py-2"
                  value={cohort}
                  onChange={(e) => setCohort(e.target.value)}
                  min="2000"
                  max="2099"
                  required
                />
              </div>
              {/* Program */}
              <div>
                <label className="block text-gray-700">Program</label>
                <Select
                  value={program ? { value: program, label: program } : null}
                  onChange={(selected) => setProgram(selected ? selected.value : "")}
                  options={[
                    { value: "Biology", label: "Biology" },
                    { value: "Business Administration", label: "Business Administration" },
                    { value: "Chemical Engineering", label: "Chemical Engineering" },
                    { value: "Chemistry", label: "Chemistry" },
                    { value: "Computer Science", label: "Computer Science" },
                    { value: "Electrical Engineering", label: "Electrical Engineering" },
                    { value: "Mechanical Engineering", label: "Mechanical Engineering" },
                    { value: "Physics", label: "Physics" },
                    { value: "Psychology", label: "Psychology" }
                  ]}
                  className="basic-select"
                  classNamePrefix="select"
                  placeholder="Select program"
                  required
                />
              </div>
            </div>
          </div>
        );
      case "address":
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">
              Contact & Address Information
            </h3>
            <div className="grid grid-cols-1 gap-4">
              {/* Email */}
              <div>
                <label className="block text-gray-700">Email</label>
                <input
                  type="email"
                  className="w-full border rounded px-3 py-2"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              {/* Phone */}
              <div>
                <label className="block text-gray-700">Phone</label>
                <input
                  type="tel"
                  className="w-full border rounded px-3 py-2"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
              </div>
              {/* Street */}
              <div>
                <label className="block text-gray-700">Street</label>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  required
                />
              </div>
              {/* City and Zip Code */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700">City</label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700">Zip Code</label>
                  <input
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    required
                  />
                </div>
              </div>
              {/* State */}
              <div>
                <label className="block text-gray-700">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
        );
      case "parent":
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Parent Information</h3>
            <div className="grid grid-cols-1 gap-4">
              {/* Parent Name */}
              <div>
                <label className="block text-gray-700">Parent Name</label>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  value={parentName}
                  onChange={(e) => setParentName(e.target.value)}
                />
              </div>
              {/* Parent Phone */}
              <div>
                <label className="block text-gray-700">Parent Phone</label>
                <input
                  type="tel"
                  className="w-full border rounded px-3 py-2"
                  value={parentPhone}
                  onChange={(e) => setParentPhone(e.target.value)}
                />
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case "student":
        if (!firstName || !lastName || !studentId || !cohort || !program) {
          toast.error("Please fill in all student information fields.");
          setActiveTab("student");
          return false;
        }
        return true;
      case "address":
        if (!email || !phone || !street || !city || !state || !zip) {
          toast.error("Please fill in all address information fields.");
          setActiveTab("address");
          return false;
        }
        return true;
      case "parent":
        if (!parentName || !parentPhone) {
          toast.error("Please fill in all parent information fields.");
          setActiveTab("parent");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateStep("student")) return;
    if (!validateStep("address")) return;
    if (validateStep("parent")) {
      const updatedStudent = {
        id: student.id,
        studentId,
        firstName,
        lastName,
        cohort: parseInt(cohort, 10),
        program,
        email,
        phone,
        address: {
          street,
          city,
          state,
          zip,
        },
        parentName,
        parentPhone,
      };

      onSubmit(updatedStudent);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-lg p-6 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <X size={20} />
        </button>
        <h2 className="text-xl font-bold mb-6">Edit Student</h2>

        {/* Tab Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-4 border-b">
            <button
              type="button"
              onClick={() => setActiveTab("student")}
              className={`py-2 px-4 ${
                activeTab === "student"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-blue-600"
              }`}
            >
              Student
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("address")}
              className={`py-2 px-4 ${
                activeTab === "address"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-blue-600"
              }`}
            >
              Address
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("parent")}
              className={`py-2 px-4 ${
                activeTab === "parent"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-blue-600"
              }`}
            >
              Parent
            </button>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {renderStepContent()}

          {/* Submit Button */}
          <div className="flex justify-end mt-6">
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded"
            >
              Update Student
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStudentModal;
